import { createInstance, dateReviver } from '@/api';
import {
    OrderDirection,
    ProductGroupClient,
    ProductGroupSearchResultDto,
    ProductProcessDefinitionSearchResultDto
} from '@/api/backoffice/clients';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { i18n } from '@/i18n';

export interface ProductGroupListFilter {
    id?: number | null;
    label?: string;
    productProcessDefinition?: ProductProcessDefinitionSearchResultDto;
    order?: string;
    orderDirection?: OrderDirection;
    pageNumber?: number;
    pageSize?: number;
}

export interface ProductGroupListStoreState {
    filter: ProductGroupListFilter;
    loading: boolean;
    items?: ProductGroupSearchResultDto[];
    totalCount?: number;
}

// Must stay in sync with the column marked as default sorted column in ProductGroupSearch.vue.
export const DEFAULT_ORDER = 'label';

export const client = new ProductGroupClient('', createInstance(), dateReviver);

export const useProductGroupListStore = defineStore('configuration-product-group-list-store', {
    state: (): ProductGroupListStoreState => ({
        filter: {
            order: DEFAULT_ORDER,
            orderDirection: OrderDirection.Ascending,
            pageNumber: 1,
            pageSize: 25,
            productProcessDefinition: null
        },
        totalCount: 0,
        loading: false,
        items: []
    }),
    getters: {
        metaData(): {
            totalRows: number;
            resultsPerPage: number;
            currentPage: number | undefined;
            serverside: boolean;
        } {
            return {
                totalRows: this.totalCount,
                resultsPerPage: this.filter.pageSize,
                currentPage: this.filter.pageNumber,
                serverside: true
            };
        },
        getProductGroup(): (id: number) => ProductGroupSearchResultDto | undefined {
            return (id: number) => this.items?.find((item) => item.id === id);
        }
    },
    actions: {
        init(filter: ProductGroupListFilter) {
            this.filter = { ...this.filter, ...filter };
            this.items = [];
        },
        clearFilter() {
            this.filter = {
                order: DEFAULT_ORDER,
                orderDirection: OrderDirection.Ascending,
                pageNumber: 1,
                pageSize: 25,
                productProcessDefinition: null
            };

            useAlertStore().clearAlert();

            this.items = [];
        },
        resetPager() {
            this.filter.pageNumber = 1;
        },
        async getProductGroups() {
            this.loading = true;
            useAlertStore().clearAlert();

            try {
                const response = await client.search(
                    this.filter.id,
                    this.filter.label,
                    // The backend still filters on description; the search page offers the
                    // product process definition instead.
                    null,
                    this.filter.productProcessDefinition?.id,
                    this.filter.order,
                    this.filter.orderDirection,
                    this.filter.pageNumber,
                    this.filter.pageSize
                );

                this.items = response.items;
                this.filter.pageNumber = response.page;
                this.filter.pageSize = response.pageSize;
                this.totalCount = response.totalCount;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('productgroup.list.error.title'), error, false);
            } finally {
                this.loading = false;
            }
        },
        updatePage(pageNumber: number) {
            this.filter.pageNumber = pageNumber;
        },
        updateSorting(column: string, order: string) {
            this.filter.order = column;
            this.filter.orderDirection = order === 'Asc' ? OrderDirection.Ascending : OrderDirection.Descending;
        }
    }
});
